import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({
    theme: 'light',
    setTheme: () => {},
    toggleTheme: () => {},
    darkIntensity: 1,
    setDarkIntensity: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        // Check localStorage first
        const stored = localStorage.getItem('ideavault-theme');
        if (stored) return stored;
        // Then check system preference
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    });
    const [darkIntensity, setDarkIntensity] = useState(() => {
        if (typeof window === 'undefined') return 1;
        const stored = window.localStorage.getItem('ideavault-dark-intensity');
        const parsed = stored ? Number(stored) : 1;
        if (Number.isNaN(parsed)) return 1;
        return Math.min(1.2, Math.max(0.8, parsed));
    });

    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);
        localStorage.setItem('ideavault-theme', theme);
    }, [theme]);

    useEffect(() => {
        if (theme === 'dark') {
            const clamped = Math.min(1.2, Math.max(0.8, darkIntensity));
            document.documentElement.style.setProperty('--dark-intensity', String(clamped));
            window.localStorage.setItem('ideavault-dark-intensity', String(clamped));
        } else {
            document.documentElement.style.removeProperty('--dark-intensity');
        }
    }, [theme, darkIntensity]);

    // Listen for system preference changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            const stored = localStorage.getItem('ideavault-theme');
            // Only auto-switch if user hasn't manually set preference
            if (!stored) {
                setTheme(e.matches ? 'dark' : 'light');
            }
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, darkIntensity, setDarkIntensity }}>
            {children}
        </ThemeContext.Provider>
    );
};
